// Comprehensive Washington State family law data based on Custody X Change and official Washington sources
const washingtonStateData = {
  'washington': {
    name: 'Washington',
    overview: {
      keyPoints: [
        'Washington follows the "best interests of the child" standard with specific factors outlined in RCW 26.09.187',
        'Washington uses "residential schedule" terminology instead of traditional custody and visitation',
        'The state has a strong preference for both parents to maintain meaningful relationships with their children',
        'Washington requires detailed parenting plans in all cases involving minor children',
        'The state presumes that frequent and continuing contact with both parents is in the child\'s best interest',
        'Washington has mandatory parent education requirements for all divorce and custody cases',
        'Relocation restrictions apply when moving more than 20 miles from the other parent'
      ],
      uniqueFeatures: [
        'Washington uses unique "residential schedule" and "decision-making" terminology rather than custody',
        'The state has a 20-mile relocation threshold - one of the strictest in the nation',
        'Washington allows temporary family law orders without a hearing in some circumstances',
        'The state has extensive alternative dispute resolution requirements before trial',
        'Washington recognizes domestic partnerships with full parental rights',
        'The state has specialized family law facilitators in most counties',
        'Washington has mandatory parenting seminars for all cases involving children'
      ]
    },
    legalRequirements: {
      residency: 'Either spouse or the child must have lived in Washington for at least 90 days before filing, or Washington must be the child\'s home state under UCCJEA',
      filing: 'File in the county where either parent resides or where the child has lived for the past 6 months',
      paternity: 'Unmarried fathers can establish paternity through acknowledgment, genetic testing, court order, or presumption',
      documentation: 'Required documents include petition, summons, parenting plan, child support worksheets, and financial declaration',
      uniqueRules: [
        'Mandatory parent education class required within 60 days of filing',
        'Alternative dispute resolution (mediation or arbitration) required before trial in most counties',
        'Washington requires detailed parenting plans addressing all aspects of child-rearing',
        'Guardian ad litem appointment is discretionary but common in contested cases',
        'Temporary orders can be obtained on an expedited basis for urgent matters'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Washington uses "residential schedule" to describe when and where the child spends time with each parent',
      legalCustody: 'Called "decision-making responsibility" - involves authority for major decisions about the child\'s welfare',
      jointCustody: 'Washington presumes joint decision-making is in the child\'s best interest unless evidence shows otherwise',
      visitation: 'Referred to as "residential time" - detailed schedules for when the child is with each parent',
      specialProvisions: [
        'Supervised residential time may be ordered when child safety is a concern',
        'Long-distance residential schedules accommodate parents living far apart',
        'Extended summer residential time is common for non-primary residential parents',
        'Holiday residential time typically alternates between parents',
        'Electronic communication (video calls, texting) is encouraged and protected',
        'Grandparent visitation rights are recognized under specific circumstances'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed residential schedule including regular rotation, weekends, holidays, and school breaks',
        'Decision-making responsibilities for education, healthcare, religious training, and extracurricular activities',
        'Methods for resolving disputes between parents including mediation requirements',
        'Restrictions on either parent if necessary for child\'s welfare',
        'Transportation arrangements and responsibility for costs',
        'Communication methods between parents and with children during residential time',
        'Relocation provisions and notification requirements for moves over 20 miles',
        'Provisions for changes to the parenting plan as children mature',
        'Guidelines for introducing new partners to children',
        'Emergency contact procedures and decision-making protocols'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures',
        'Include detailed provisions for Washington\'s weather conditions affecting travel',
        'Address how to handle missed residential time due to illness, weather, or emergencies',
        'Specify meeting locations that are convenient and safe for exchanges',
        'Include provisions for the child\'s participation in activities and school events',
        'Address how major decisions will be made and communicated between parents',
        'Plan for Washington\'s unique geography and potential travel distances',
        'Include technology use guidelines for communication during residential time'
      ]
    },
    childSupport: {
      guidelines: 'Washington uses a modified Income Shares Model with the Washington State Child Support Schedule based on combined net income',
      factors: [
        'Net monthly income of both parents after taxes and mandatory deductions',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Residential time with each parent (significant time can reduce support)',
        'Special needs of the children requiring additional expenses'
      ],
      calculation: 'Washington Child Support Schedule using combined net income and proportional allocation',
      modifications: 'Substantial change in circumstances required, typically 25% change in support amount or significant change in income or residential time',
      enforcement: 'Strong enforcement through Washington State Department of Social and Health Services including wage garnishment, tax intercept, license suspension, and asset seizure',
      detailedInfo: {
        basicCalculation: {
          description: 'Washington uses a modified Income Shares Model based on the principle that children should receive the same proportion of parental income they would have received if the family remained intact.',
          steps: [
            'Calculate each parent\'s gross monthly income from all sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate net monthly income for each parent after allowable deductions',
            'Add both parents\' net incomes to determine combined net monthly income',
            'Use the Washington State Child Support Schedule to find basic support obligation',
            'Allocate support proportionally based on each parent\'s income percentage',
            'Add costs for health insurance, childcare, and extraordinary expenses',
            'Apply any adjustments for significant residential time (more than 25%)',
            'Calculate final monthly support amount'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Royalties, patents, and intellectual property income',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, etc.)',
            'Foster care payments for non-biological children',
            'Child support received for other children',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or domestic partner',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Reimbursement for job-related expenses'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status and exemptions',
            'State income taxes (Washington has no state income tax)',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        residentialTimeAdjustment: {
          description: 'Washington allows adjustment to child support when the paying parent has significant residential time with the child.',
          thresholds: [
            'No adjustment for less than 25% residential time (91 nights per year)',
            'Potential adjustment for 25-35% residential time (91-127 nights per year)',
            'Standard adjustment for 35%+ residential time (128+ nights per year)',
            'Shared residential schedule may result in no support or offset calculations'
          ],
          calculation: 'Adjustment considers additional direct costs during residential time and reduced costs to primary residential parent',
          factors: [
            'Actual overnight residential time over previous 12 months',
            'Additional housing costs for accommodating child during residential time',
            'Duplicate expenses (clothing, toys, school supplies) in both homes',
            'Transportation costs for residential schedule exchanges',
            'Reduced household expenses for primary residential parent'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically for covering the child',
            allocation: 'Added to basic support amount - paying parent responsible for reasonable cost',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage',
              'Insurance must be available through employer or Washington Health Benefit Exchange',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance information and cards'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs for employment, education, or job training',
            allocation: 'Added to basic support amount - paying parent responsible for proportional share',
            requirements: 'Must be work-related or education-related necessity',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for local area and type of care',
              'Before and after school care typically included',
              'Summer childcare during school breaks may be included',
              'Documentation and receipts required'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance',
              'Orthodontic treatment and appliances',
              'Vision care including glasses and contact lenses',
              'Mental health counseling and therapy',
              'Physical therapy and rehabilitation',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies',
              'Treatment for chronic conditions',
              'Emergency medical treatment',
              'Specialized medical consultations'
            ],
            allocation: 'Shared proportionally based on parents\' income percentages',
            procedure: 'Parent should seek advance approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment',
            examples: [
              'Youth sports teams, equipment, and travel',
              'Music lessons and instrument rental',
              'Dance, martial arts, or gymnastics classes',
              'Summer camps and day programs',
              'School trips and educational activities',
              'Tutoring and educational support',
              'Art, theater, or other creative programs'
            ],
            note: 'Not automatically included - must be agreed upon or court-ordered',
            considerations: [
              'Activities should benefit the child and be age-appropriate',
              'Costs should be reasonable and within family means',
              'Both parents should have input on major activity decisions'
            ]
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 25% or more)',
            'Significant change in residential schedule',
            'Change in health insurance costs or availability',
            'Change in childcare expenses',
            'Child\'s needs change significantly',
            'Emancipation or change in number of children',
            'More than 12 months since last order review'
          ],
          process: [
            'File Motion for Modification of Child Support',
            'Serve other parent with motion and supporting documents',
            'Provide updated financial declarations and worksheets',
            'Attend required mediation if ordered',
            'Present case at court hearing',
            'Obtain modified support order'
          ],
          timeline: 'Modifications effective from date of filing motion, not final order date',
          documentation: [
            'Updated Financial Declaration',
            'Current Child Support Worksheets',
            'Recent pay stubs and tax returns',
            'Documentation of changed circumstances',
            'Current childcare and insurance costs'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and wage garnishment',
            'Tax refund intercept (state and federal)',
            'Asset seizure and bank account levies',
            'License suspension (driver\'s, professional, recreational)',
            'Credit bureau reporting',
            'Contempt of court proceedings',
            'Property liens and asset seizure',
            'Passport denial for large arrearages',
            'Lottery and gaming winnings intercept'
          ],
          interest: 'Unpaid support accrues interest at 12% per annum on past-due amounts',
          statute: 'No statute of limitations on collecting past-due child support',
          resources: [
            'Washington State Division of Child Support',
            'Local prosecuting attorney offices',
            'Private collection agencies contracted by state',
            'Interstate enforcement agencies'
          ]
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'Washington State Courts - Family Law',
          url: 'https://www.courts.wa.gov/newsinfo/index.cfm?fa=newsinfo.internetresources&altmenu=fami',
          description: 'Official Washington State court system family law resources with forms and procedures'
        },
        {
          name: 'Washington Law Help',
          url: 'https://www.washingtonlawhelp.org/issues/family-law',
          description: 'Comprehensive self-help resources for Washington family law cases'
        },
        {
          name: 'King County Superior Court Family Law',
          url: 'https://www.kingcounty.gov/courts/superior-court/family.aspx',
          description: 'Family law information and services for Seattle and King County',
          address: '516 Third Avenue, Seattle, WA 98104',
          phone: '(206) 477-1617'
        },
        {
          name: 'Pierce County Superior Court Family Law',
          url: 'https://www.piercecountywa.gov/1338/Family-Law',
          description: 'Family court services for Tacoma and Pierce County',
          address: '930 Tacoma Avenue S, Tacoma, WA 98402',
          phone: '(253) 798-7400'
        },
        {
          name: 'Snohomish County Superior Court Family Law',
          url: 'https://www.snohomishcountywa.gov/1127/Family-Court-Services',
          description: 'Family court services for Everett and Snohomish County',
          address: '3000 Rockefeller Avenue, Everett, WA 98201',
          phone: '(425) 388-3421'
        },
        {
          name: 'Spokane County Superior Court Family Law',
          url: 'https://www.spokanecounty.org/1887/Family-Court',
          description: 'Family court services for Spokane and eastern Washington',
          address: '1116 West Broadway Avenue, Spokane, WA 99260',
          phone: '(509) 477-2211'
        }
      ],
      support: [
        {
          name: 'Washington State Division of Child Support',
          url: 'https://www.dshs.wa.gov/esa/division-child-support',
          phone: '1-800-457-6202',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Northwest Justice Project',
          url: 'https://nwjustice.org/home',
          phone: '1-888-201-1014',
          description: 'Free legal services for low-income individuals in family law matters'
        },
        {
          name: 'Washington State Bar Association Family Law Section',
          url: 'https://www.wsba.org/legal-community/sections/family-law',
          description: 'Professional resources and attorney referrals for family law'
        },
        {
          name: 'Washington State Coalition Against Domestic Violence',
          url: 'https://wscadv.org/',
          phone: '1-800-562-6025',
          description: 'Statewide domestic violence resources and legal advocacy'
        },
        {
          name: 'Fathers & Families Coalition of America - Washington Chapter',
          url: 'https://www.fathersandfamilies.org/',
          description: 'Advocacy and support for fathers\' rights in Washington'
        }
      ],
      legal: [
        {
          name: 'Washington State Bar Association Lawyer Referral Service',
          url: 'https://www.wsba.org/for-the-public/lawyer-referral-service',
          phone: '1-800-945-9722',
          description: 'Professional attorney referrals for family law cases throughout Washington'
        },
        {
          name: 'University of Washington School of Law Clinics',
          url: 'https://www.law.washington.edu/clinics/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Seattle University School of Law Access to Justice Institute',
          url: 'https://law.seattleu.edu/centers-and-institutes/access-to-justice-institute',
          description: 'Clinical legal services and family law assistance'
        },
        {
          name: 'Gonzaga University School of Law Clinical Programs',
          url: 'https://www.gonzaga.edu/law/clinical-programs',
          description: 'Legal clinic services in eastern Washington'
        },
        {
          name: 'Washington Collaborative Law Group',
          url: 'https://www.collaborativelaw.org/find-a-professional/washington/',
          description: 'Network of collaborative divorce professionals in Washington'
        }
      ],
      educational: [
        {
          name: 'Parenting Seminars Washington',
          url: 'https://www.courts.wa.gov/newsinfo/index.cfm?fa=newsinfo.internetresources&altmenu=pare',
          description: 'Mandatory parent education seminars for all divorce and custody cases'
        },
        {
          name: 'Washington Parenting Plan Resources',
          url: 'https://www.washingtonlawhelp.org/resource/parenting-plans-in-washington-state',
          description: 'Comprehensive guide to creating parenting plans in Washington'
        },
        {
          name: 'Washington Guardian ad Litem Program',
          url: 'https://www.courts.wa.gov/programs_orgs/guardian/',
          description: 'Information about guardian ad litem services for children'
        },
        {
          name: 'Washington State University Extension - Family Life',
          url: 'https://extension.wsu.edu/families/',
          description: 'Educational programs for co-parenting and family relationships'
        },
        {
          name: 'Family Court Services Programs',
          url: 'https://www.courts.wa.gov/newsinfo/index.cfm?fa=newsinfo.internetresources&altmenu=fcs',
          description: 'Court-connected services including mediation and evaluation'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'King County Superior Court',
            address: '516 Third Avenue, Seattle, WA 98104',
            phone: '(206) 477-1617',
            jurisdiction: 'Seattle, Bellevue, Renton, Kent, and all of King County'
          },
          {
            name: 'Pierce County Superior Court',
            address: '930 Tacoma Avenue S, Tacoma, WA 98402',
            phone: '(253) 798-7400',
            jurisdiction: 'Tacoma, Lakewood, Puyallup, and all of Pierce County'
          },
          {
            name: 'Snohomish County Superior Court',
            address: '3000 Rockefeller Avenue, Everett, WA 98201',
            phone: '(425) 388-3421',
            jurisdiction: 'Everett, Edmonds, Lynnwood, and all of Snohomish County'
          },
          {
            name: 'Spokane County Superior Court',
            address: '1116 West Broadway Avenue, Spokane, WA 99260',
            phone: '(509) 477-2211',
            jurisdiction: 'Spokane, Spokane Valley, and eastern Washington'
          },
          {
            name: 'Clark County Superior Court',
            address: '1200 Franklin Street, Vancouver, WA 98660',
            phone: '(360) 397-2292',
            jurisdiction: 'Vancouver, Camas, Battle Ground, and all of Clark County'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$280.00',
          'Divorce (without children)': '$280.00',
          'Custody/Parenting Plan': '$240.00',
          'Child Support': '$240.00',
          'Modification': '$240.00',
          'Paternity': '$240.00',
          'Protection Order': 'No fee'
        },
        requiredForms: [
          'Petition for Dissolution or Legal Separation',
          'Summons',
          'Parenting Plan',
          'Child Support Worksheets',
          'Financial Declaration',
          'Certificate of Service',
          'Decree of Dissolution'
        ]
      },
      statutes: [
        {
          name: 'Washington Revised Code Title 26 - Domestic Relations',
          url: 'https://app.leg.wa.gov/rcw/default.aspx?cite=26',
          description: 'Primary statutes governing marriage, divorce, and family law in Washington'
        },
        {
          name: 'Washington Child Support Schedule (RCW 26.19)',
          url: 'https://app.leg.wa.gov/rcw/default.aspx?cite=26.19',
          description: 'Official child support guidelines and calculation methods'
        },
        {
          name: 'Washington Parenting Act (RCW 26.09)',
          url: 'https://app.leg.wa.gov/rcw/default.aspx?cite=26.09',
          description: 'Comprehensive parenting plan and residential schedule laws'
        },
        {
          name: 'Washington Uniform Child Custody Jurisdiction Act (RCW 26.27)',
          url: 'https://app.leg.wa.gov/rcw/default.aspx?cite=26.27',
          description: 'Interstate custody jurisdiction and enforcement'
        }
      ],
      emergencyContacts: [
        {
          name: 'Washington State Domestic Violence Hotline',
          phone: '1-800-562-6025',
          description: '24/7 statewide domestic violence crisis line'
        },
        {
          name: 'Washington State Child Abuse Hotline',
          phone: '1-866-END-HARM',
          description: '24/7 child abuse and neglect reporting'
        },
        {
          name: 'Crisis Text Line',
          phone: 'Text HOME to 741741',
          description: '24/7 crisis support via text message'
        }
      ]
    },
    bestInterestsFactors: [
      'The strength, nature, and stability of the child\'s relationship with each parent',
      'The agreements of the parties, provided they were entered into knowingly and voluntarily',
      'Each parent\'s past and potential for future performance of parenting functions',
      'The emotional needs and developmental level of the child',
      'The child\'s relationship with siblings and with other significant adults',
      'The wishes of a child who is sufficiently mature to express reasoned and independent preferences',
      'Each parent\'s employment schedule and other practical considerations',
      'The extent and nature of involvement of other persons in the child\'s life',
      'The history of domestic violence, if any, including consideration of the proximity of the perpetrator',
      'The history of substance abuse, if any, including steps taken to address the abuse'
    ],
    practicalTips: [
      'Washington courts require extremely detailed parenting plans - be thorough and specific',
      'Complete the mandatory parent education seminar as early as possible in your case',
      'Consider mediation or collaborative law - Washington courts strongly favor negotiated agreements',
      'Document your involvement in your child\'s daily activities, school, and healthcare',
      'Be prepared for the 20-mile relocation restriction - it\'s strictly enforced',
      'Keep detailed records of residential time and any deviations from the schedule',
      'Washington judges appreciate parents who demonstrate flexibility and child-focus',
      'Take advantage of Washington\'s family law facilitators available in most counties'
    ]
  }
};

export default washingtonStateData;